/**
 * chartResults.jsx - Composant de graphique SVG interactif
 * 
 * Affiche un histogramme avec :
 * - Axes X (mois) et Y (montant) avec graduations
 * - Barres représentant l'évolution de l'épargne
 * - Ligne d'objectif en pointillés
 * - Tooltip interactif au hover (montant + mensualité)
 * - Échantillonnage automatique si trop de points (max 40 barres)
 * - Responsive via viewBox SVG
 * 
 * Algorithme :
 * 1. Échantillonnage : si > maxBars points, prendre 1 point sur N
 * 2. Calcul des échelles X et Y
 * 3. Positionnement des barres et axes
 * 4. Gestion du tooltip avec position dynamique
 * 
 * Props :
 * @param {Array<{month: number, amount: number}>} data - Points de données
 * @param {number} goal - Montant objectif (pour la ligne)
 * @param {string} currency - Code devise (EUR, USD, etc.)
 * @param {number} monthly - Mensualité (affichée dans le tooltip)
 * @param {number} [maxBars=40] - Nombre max de barres à afficher
 * @param {string} [className=""] - Classes CSS supplémentaires
 */

import { useMemo, useState } from "react";
import "./chart-results.css";

/**
 * Composant ChartResults - Graphique SVG d'histogramme interactif
 * @returns {JSX.Element} Graphique avec tooltip
 */
export default function ChartResults({
  data,
  goal,
  currency,
  monthly,
  maxBars = 40,
  className = "",
}) {
  // État du tooltip : {x, y, month, amount} ou null
  const [tip, setTip] = useState(null);

  // Formateur monétaire (mise en cache avec useMemo)
  const MONEY = useMemo(
    () =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency,
        maximumFractionDigits: 0, // Pas de décimales
      }),
    [currency]
  );

  // Configuration du graphique (dimensions, échelles, échantillonnage)
  const cfg = useMemo(() => {
    // Dimensions du SVG
    const width = 720;
    const height = 300;
    
    // Marges (padding) pour les axes et labels
    const padL = 56;  // Gauche (pour les labels Y)
    const padR = 20;  // Droite
    const padT = 20;  // Haut
    const padB = 36;  // Bas (pour les labels X)

    // Sécurisation des données (au moins 1 point)
    const safeData = Array.isArray(data) && data.length ? data : [{ month: 0, amount: 0 }];

    // Échantillonnage si trop de points (garder 1 point sur N)
    // Exemple : 200 points avec maxBars=40 → step=5 → 1 point sur 5
    const step = Math.max(1, Math.ceil(safeData.length / maxBars));
    const sampled = safeData.filter((_, i) => i % step === 0);

    // Toujours inclure le dernier point (important pour la fin de la courbe)
    if (sampled[sampled.length - 1]?.month !== safeData[safeData.length - 1]?.month) {
      sampled.push(safeData[safeData.length - 1]);
    }

    // Calcul des valeurs max pour les échelles
    const maxX = sampled[sampled.length - 1]?.month || 1; // Mois max
    const maxY = Math.max(goal || 0, ...sampled.map((d) => d.amount), 1); // Montant max

    // Dimensions de la zone de graphique (sans les marges)
    const chartW = width - padL - padR;
    const chartH = height - padT - padB;

    // Fonctions d'échelle pour convertir données → coordonnées SVG
    const x = (m) => padL + (m / maxX) * chartW;  // Mois → position X
    const y = (v) => padT + (1 - v / maxY) * chartH; // Montant → position Y (inversé)

    // Calcul de la largeur des barres (avec espacement)
    const barCount = sampled.length;
    const gap = 6; // Espacement entre les barres
    const barW = Math.max(6, Math.floor(chartW / barCount) - gap); // Minimum 6px

    return { width, height, padL, padR, padT, padB, chartW, chartH, x, y, maxX, maxY, sampled, barW };
  }, [data, goal, maxBars]);

  /**
   * Affiche le tooltip à la position de la souris
   * @param {MouseEvent} evt - Événement souris
   * @param {Object} p - Point de données {month, amount}
   */
  function showTip(evt, p) {
    // Trouver le conteneur du graphique pour calculer la position relative
    const wrapper = evt.currentTarget.closest(".chartres-wrapper");
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    setTip({
      x: evt.clientX - rect.left, // Position X relative au wrapper
      y: evt.clientY - rect.top,  // Position Y relative au wrapper
      month: p.month,
      amount: p.amount,
    });
  }

  /**
   * Met à jour la position du tooltip lors du déplacement de la souris
   * @param {MouseEvent} evt - Événement souris
   */
  function moveTip(evt) {
    if (!tip) return; // Pas de tooltip actif
    const wrapper = evt.currentTarget.closest(".chartres-wrapper");
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    setTip((t) =>
      t
        ? {
            ...t,
            x: evt.clientX - rect.left, // Mettre à jour X
            y: evt.clientY - rect.top,  // Mettre à jour Y
          }
        : null
    );
  }

  /**
   * Masque le tooltip
   */
  function hideTip() {
    setTip(null);
  }

  // Nombre de graduations sur les axes
  const yTicks = 5; // Axe Y (montants)
  const xTicks = 5; // Axe X (mois)

  return (
    <div className={`chartres-wrapper ${className}`}>
      <svg
        className="chartres-svg"
        viewBox={`0 0 ${cfg.width} ${cfg.height}`}
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={moveTip}  // Suivre le déplacement de la souris
        onMouseLeave={hideTip} // Masquer le tooltip quand on quitte le SVG
      >
        {/* Axe Y (vertical, à gauche) */}
        <line
          x1={cfg.padL}
          y1={cfg.padT}
          x2={cfg.padL}
          y2={cfg.padT + cfg.chartH}
          className="chartres-axis"
        />
        {/* Axe X */}
        <line
          x1={cfg.padL}
          y1={cfg.padT + cfg.chartH}
          x2={cfg.padL + cfg.chartW}
          y2={cfg.padT + cfg.chartH}
          className="chartres-axis"
        />

        {/* Graduations Y (montants) */}
        {Array.from({ length: yTicks + 1 }).map((_, i) => {
          const value = (cfg.maxY / yTicks) * i; // Valeur pour cette graduation
          const yy = cfg.y(value);                // Position Y
          return (
            <g key={`y-${i}`}>
              {/* Petit trait de graduation */}
              <line
                x1={cfg.padL - 6}
                x2={cfg.padL}
                y1={yy}
                y2={yy}
                className="chartres-tick"
              />
              {/* Label du montant */}
              <text
                x={cfg.padL - 10}
                y={yy + 4}
                textAnchor="end"
                className="chartres-label"
              >
                {MONEY.format(value)}
              </text>
            </g>
          );
        })}

        {/* Graduations X (mois) */}
        {Array.from({ length: xTicks + 1 }).map((_, i) => {
          const month = Math.round((cfg.maxX / xTicks) * i);
          const xx = cfg.x(month);
          return (
            <g key={`x-${i}`}>
              <line
                x1={xx}
                x2={xx}
                y1={cfg.padT + cfg.chartH}
                y2={cfg.padT + cfg.chartH + 6}
                className="chartres-tick"
              />
              <text
                x={xx}
                y={cfg.padT + cfg.chartH + 20}
                textAnchor="middle"
                className="chartres-label"
              >
                {month} m
              </text>
            </g>
          );
        })}

        {/* Ligne objectif (trait horizontal en pointillés) */}
        <line
          x1={cfg.padL}
          x2={cfg.padL + cfg.chartW}
          y1={cfg.y(goal)}
          y2={cfg.y(goal)}
          className="chartres-goal"
        />

        {/* Barres de l'histogramme */}
        {cfg.sampled.map((p, idx) => {
          const bx = cfg.x(p.month) - cfg.barW / 2; // Position X (centrée)
          const by = cfg.y(p.amount);                // Position Y (haut de la barre)
          const bh = cfg.padT + cfg.chartH - by;     // Hauteur de la barre

          return (
            <rect
              key={`${p.month}-${idx}`}
              x={bx}
              y={by}
              width={cfg.barW}
              height={bh}
              rx="6" // Coins arrondis
              className="chartres-bar"
              onMouseEnter={(e) => showTip(e, p)} // Afficher le tooltip
              onMouseMove={(e) => showTip(e, p)}  // Mettre à jour la position
            />
          );
        })}
      </svg>

      {/* Tooltip (affiché uniquement si tip est défini) */}
      {tip && (
        <div
          className="chartres-tooltip"
          style={{
            left: tip.x + 12,
            top: tip.y - 10,
          }}
        >
          <div className="chartres-tooltip-title">+{tip.month} mois</div>
          <div className="chartres-tooltip-row">
            <span>Montant</span>
            <b>{MONEY.format(tip.amount)}</b>
          </div>
          <div className="chartres-tooltip-row">
            <span>Mensualité</span>
            <b>{MONEY.format(monthly)}</b>
          </div>
        </div>
      )}
    </div>
  );
}
